import {BadInvocationRequest} from '@/model/wallets/types/WalletErrors'

const cloneJson = <T>(value: T): T => JSON.parse(JSON.stringify(value))

/** NEP-21 / dAPI expects signer `account` as a script hash without the `0x` prefix. */
const normalizeSignerAccount = (account: unknown) => {
  if (typeof account !== 'string' || !account) {
    return account
  }

  return account.startsWith('0x') ? account.slice(2) : account
}

const normalizeSigners = (signers: unknown) => {
  if (!Array.isArray(signers)) {
    return signers
  }

  return signers.map(signer => {
    if (!signer || typeof signer !== 'object') {
      return signer
    }

    const entry = signer as Record<string, unknown>

    return {
      ...entry,
      account: normalizeSignerAccount(entry.account),
    }
  })
}

/** Convert a `ContractInvocationMulti` payload into the NEP-21 multi-invoke shape. */
export const toNep21MultiPayload = (payload: Record<string, unknown>) => {
  const invocations = cloneJson(payload.invocations ?? payload.invokeArgs)

  if (!Array.isArray(invocations) || invocations.length === 0) {
    return payload
  }

  return {
    fee: payload.fee,
    extraSystemFee: payload.extraSystemFee,
    overrideSystemFee: payload.overrideSystemFee,
    broadcastOverride: payload.broadcastOverride,
    signers: normalizeSigners(cloneJson(payload.signers)),
    invocations,
  }
}

/**
 * Send an invocation through a NEP-21 dAPI provider, trying the method names
 * used across compliant wallets and returning the resulting txid.
 */
export const invokeNep21Provider = async (
  provider: any,
  payload: Record<string, unknown>
): Promise<string> => {
  const nep21Payload = toNep21MultiPayload(payload)

  for (const method of [
    'invokeFunction',
    'invokeMulti',
    'invokeMultiple',
  ] as const) {
    if (typeof provider[method] !== 'function') continue

    const result = await provider[method](nep21Payload)
    if (result?.txid) return result.txid
  }

  if (typeof provider.invoke === 'function') {
    const result = await provider.invoke(nep21Payload)
    if (typeof result === 'string') return result
    if (result?.txid) return result.txid
  }

  throw BadInvocationRequest
}
